import { useEffect, useMemo } from 'react';
import Head from 'next/head';
import { Provider } from 'react-redux';
import { ApolloProvider } from '@apollo/client';
import { LDProvider } from 'launchdarkly-react-client-sdk';

import '../styles/root.scss';
import { initializeAndStartDD } from '../utils/datadog/datadog';
import withReduxStore from 'utils/redux/wrapper';
import initApollo from 'utils/apollo';
import ThemeProvider from 'design-system/theme';
import { AppMainComponent } from 'modules/shared/components/AppMainComponent';
import { RouteChangeIndicator } from 'modules/shared/components/RouteChangeIndicator';
import { ErrorBoundary } from 'modules/shared/components/ErrorBoundary';
import { AuthContextProvider } from 'modules/shared/context/AuthContextProvider';
import { ldProviderConfig } from 'utils/launchdarkly/providerConfig';
import { readDevServerBootstrap } from 'utils/launchdarkly/devServerBootstrap';

import type { ReactElement, ReactNode } from 'react';
import type { NextPage } from 'next';
import type { AppProps } from 'next/app';
import type { Store, AnyAction } from 'redux';
import type { LDDevServerBootstrap } from 'utils/launchdarkly/devServerBootstrap';

export type NextPageWithLayout<P = object, IP = P> = NextPage<P, IP> & {
  getLayout?: (page: ReactElement) => ReactNode;
};

export type CustomAppProps = AppProps & {
  Component: NextPageWithLayout;
  reduxStore: Store<unknown, AnyAction>;
  /**
   * This stack's LaunchDarkly Dev Server flag set, injected into the SERVER
   * render by `_document.page.js` via `enhanceApp`. Absent on every ordinary
   * deployment, and absent in the browser - see `devServerBootstrap` for both
   * halves of how it travels.
   */
  ldDevServerBootstrap?: LDDevServerBootstrap;
};

const apolloClient = initApollo();

const Spring = ({ Component, pageProps, reduxStore, router, ldDevServerBootstrap }: CustomAppProps) => {
  // Hosted LaunchDarkly everywhere, except on a stack that rendered its own Dev
  // Server - in which case this is the flag set the server render already
  // fetched. `providerConfig.ts` owns that choice and the reasons for it.
  //
  // The prop is the SERVER's copy and the DOM element is the BROWSER's copy
  // of the same bytes, so both renders configure the SDK identically and hydration
  // matches. The prop is preferred rather than checked second because it is the
  // only one that exists during SSR; the element is also what carries the flags
  // across client-side navigations, which never re-run `_document`.
  const devServerBootstrap = ldDevServerBootstrap ?? readDevServerBootstrap();
  // Memoised on the payload so a re-render cannot hand `LDProvider` a fresh
  // config object, and so the Dev Server notice is logged once per page load
  // rather than once per render.
  const ldConfig = useMemo(() => ldProviderConfig(devServerBootstrap), [devServerBootstrap]);

  useEffect(() => {
    localStorage.setItem('initialTarget', window.location.pathname);
    initializeAndStartDD(router);

    return () => {
      localStorage.removeItem('initialTarget');
    };
  }, [router]);

  const getLayout = Component.getLayout ?? ((page: ReactNode) => page);

  return (
    <LDProvider {...ldConfig}>
      <Head>
        <title>Spring Health</title>
        <meta content="width=device-width,initial-scale=1" name="viewport" />
      </Head>
      <ApolloProvider client={apolloClient}>
        <Provider store={reduxStore}>
          <ThemeProvider>
            <RouteChangeIndicator />
            <ErrorBoundary>
              <AuthContextProvider>
                <AppMainComponent>
                  {getLayout(<Component url={{ query: router.query, pathname: router.pathname }} {...pageProps} />)}
                </AppMainComponent>
              </AuthContextProvider>
            </ErrorBoundary>
          </ThemeProvider>
        </Provider>
      </ApolloProvider>
    </LDProvider>
  );
};

// `<LDProvider>` is rendered above rather than applied here through
// `withLDProvider`, because that HOC freezes its config the first time this
// module is evaluated - once per PROCESS - and a Dev Server bootstrap is
// per-request data. The HOC does nothing else: it renders the same
// `<LDProvider {...config}>` with `reactOptions` defaults merged, which
// `LDProvider` merges for itself anyway (`provider.tsx`'s `getReactOptions`).
export default withReduxStore(Spring);
